import {
  HomeIcon, TruckIcon, BoxIcon, TimerIcon, UsersIcon, DashboardIcon,
  TrendIcon, ReportIcon, AuditIcon, SettingsIcon, HelpIcon, DatabaseIcon,
} from '../icons';
import { ComponentType, SVGProps } from 'react';
import { LccRole } from '../auth/types';

export interface ModuleDefinition {
  id: string;
  label: string;
  path: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Aparece en la barra inferior fija (maximo recomendado: 4, + "Mas"). */
  inBottomNav: boolean;
  /** Modulo implementado o placeholder "Proximamente". */
  status: 'active' | 'soon';
}

/**
 * Registro central de modulos de la Suite LCC.
 * Principio Open/Closed: para agregar un modulo nuevo se agrega UNA entrada aqui
 * y se crea su carpeta en /modules — no se modifica la navegacion ni el shell.
 */
export const MODULES: ModuleDefinition[] = [
  { id: 'home', label: 'Inicio', path: '/control-operativo', icon: HomeIcon, inBottomNav: true, status: 'active' },
  { id: 'reports', label: 'Reportes', path: '/reportes', icon: ReportIcon, inBottomNav: true, status: 'active' },
  { id: 'audit', label: 'Auditoría', path: '/auditoria', icon: AuditIcon, inBottomNav: true, status: 'active' },
  { id: 'operation-times', label: 'Tiempos', path: '/tiempos', icon: TimerIcon, inBottomNav: true, status: 'active' },
  { id: 'operators', label: 'Operarios', path: '/operarios', icon: UsersIcon, inBottomNav: false, status: 'soon' },
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, inBottomNav: false, status: 'soon' },
  { id: 'kpis', label: 'KPIs', path: '/kpis', icon: TrendIcon, inBottomNav: false, status: 'soon' },
  { id: 'settings', label: 'Configuración', path: '/configuracion', icon: SettingsIcon, inBottomNav: false, status: 'active' },
  { id: 'consolidado', label: 'Consolidado', path: '/consolidado', icon: DatabaseIcon, inBottomNav: false, status: 'active' },
  { id: 'help', label: 'Ayuda', path: '/ayuda', icon: HelpIcon, inBottomNav: false, status: 'soon' },
];

/**
 * Ids de "pantalla" asignables por usuario (Configuracion > Usuarios > Pantallas).
 * Home y Ayuda NO se restringen: siempre visibles para cualquier autenticado.
 * Debe coincidir con apps/api/src/shared/modules.ts (ASSIGNABLE_MODULES).
 */
export const ASSIGNABLE_MODULE_IDS = ['operation-times', 'reports', 'audit', 'dashboard', 'kpis', 'operators'] as const;
export type AssignableModuleId = (typeof ASSIGNABLE_MODULE_IDS)[number];

export const ASSIGNABLE_MODULES = MODULES.filter((m) => (ASSIGNABLE_MODULE_IDS as readonly string[]).includes(m.id));

/**
 * Modulos exclusivos de Administrador: NUNCA se muestran ni se otorgan a otro
 * rol, ni siquiera via el override individual de "Pantallas" - a diferencia
 * de los ASSIGNABLE_MODULES (Reportes/Auditoria/etc), que si se le pueden
 * conceder puntualmente a un Operador/Supervisor. Configuracion administra
 * usuarios, roles y actividades: es un modulo de administracion del sistema,
 * no una "pantalla de trabajo" mas.
 */
export const ADMIN_ONLY_MODULE_IDS = ['settings', 'consolidado'] as const;

/** Pantallas por defecto de cada rol (usadas cuando el usuario no tiene override propio). */
export const ROLE_DEFAULT_MODULES: Record<LccRole, AssignableModuleId[]> = {
  admin: ['operation-times', 'reports', 'audit', 'dashboard', 'kpis', 'operators'],
  supervisor: ['operation-times', 'reports', 'audit'],
  operator: ['operation-times'],
};

/** Pantallas efectivas: Admin siempre ve todo; si hay override, reemplaza al set del rol. */
export function resolveEffectiveModules(role: LccRole, allowedModules: string[] | null): string[] {
  if (role === 'admin') return ROLE_DEFAULT_MODULES.admin;
  if (allowedModules && allowedModules.length > 0) return allowedModules;
  return ROLE_DEFAULT_MODULES[role];
}

export function visibleModules(all: ModuleDefinition[], effective: string[], role: LccRole): ModuleDefinition[] {
  return all.filter((m) => {
    if ((ADMIN_ONLY_MODULE_IDS as readonly string[]).includes(m.id)) return role === 'admin';
    if ((ASSIGNABLE_MODULE_IDS as readonly string[]).includes(m.id)) return effective.includes(m.id);
    return true;
  });
}
